import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { IconSvg } from '@prism/icon';
import Spinner from '@prism/spinner';
import classnames from 'classnames';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var usePanel = function usePanel(props) {
  var _classnames;

  var adaTimeoutGlyph = props.adaTimeoutGlyph,
      adaTimeoutLabel = props.adaTimeoutLabel,
      className = props.className,
      color = props.color,
      glyph = props.glyph,
      inline = props.inline,
      isStatic = props.isStatic,
      label = props.label,
      size = props.size,
      showMask = props.showMask,
      targetRef = props.targetRef;
  var computedSize;

  if (size) {
    computedSize = size;
  } else if (targetRef) {
    computedSize = targetRef.current.tagName.match(/body|html/i) ? 'xl' : 'lg';
  } else if (inline) {
    computedSize = 'xs';
  } else {
    computedSize = 'md';
  }

  var panelClassName = classnames((_classnames = {}, _defineProperty(_classnames, className, className), _defineProperty(_classnames, "interstitial", true), _defineProperty(_classnames, 'interstitial-dark', color === 'dark'), _defineProperty(_classnames, 'interstitial-inline', inline), _defineProperty(_classnames, "interstitial-".concat(computedSize), computedSize), _defineProperty(_classnames, 'interstitial-panel', true), _defineProperty(_classnames, 'interstitial-panel-dark', color === 'dark'), _defineProperty(_classnames, 'interstitial-panel-light', color === 'light'), _defineProperty(_classnames, "interstitial-panel-".concat(computedSize), computedSize), _classnames));
  var maskClassName = classnames({
    'interstitial-mask': true,
    'interstitial-mask-dark': color === 'light',
    'interstitial-mask-light': color === 'dark'
  });
  var iconSizes = {
    xs: '15px',
    sm: '19px',
    md: '25px',
    lg: '33px',
    xl: '47px',
    xxl: '47px'
  };
  var iconHeight = iconSizes[computedSize] || iconSizes.xs;
  var timeoutGraphic = /*#__PURE__*/React.createElement(IconSvg, {
    glyph: adaTimeoutGlyph,
    color: null,
    height: iconHeight
  });
  var graphic = isStatic ? /*#__PURE__*/React.createElement(IconSvg, {
    glyph: glyph,
    color: null,
    height: iconHeight
  }) : /*#__PURE__*/React.createElement(Spinner, {
    color: null,
    size: computedSize
  });
  return {
    panelClassName: panelClassName,
    graphic: graphic,
    showMask: showMask,
    maskClassName: maskClassName,
    showLabel: !inline,
    label: label,
    timeout: {
      graphic: timeoutGraphic,
      label: adaTimeoutLabel
    }
  };
};

var _excluded$1 = ["adaTimeoutAfter"];

var Panel = function Panel(props) {
  var adaTimeoutAfter = props.adaTimeoutAfter,
      rest = _objectWithoutProperties(props, _excluded$1);

  var timeoutTimerRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isTimedOut = _useState2[0],
      setIsTimedOut = _useState2[1];

  var clearTimer = function clearTimer() {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
    }

    timeoutTimerRef.current = null;
  };

  var startTimer = function startTimer() {
    clearTimer();

    if (adaTimeoutAfter && adaTimeoutAfter > 0) {
      setIsTimedOut(false);
      timeoutTimerRef.current = setTimeout(function () {
        setIsTimedOut(true);
      }, adaTimeoutAfter);
    }
  };

  useEffect(function () {
    startTimer();
    return function () {
      clearTimer();
    };
  }, []);

  var _usePanel = usePanel(_objectSpread2({}, rest)),
      panelClassName = _usePanel.panelClassName,
      graphic = _usePanel.graphic,
      showMask = _usePanel.showMask,
      maskClassName = _usePanel.maskClassName,
      showLabel = _usePanel.showLabel,
      label = _usePanel.label,
      timeout = _usePanel.timeout;

  return /*#__PURE__*/React.createElement(React.Fragment, null, showMask && /*#__PURE__*/React.createElement("span", {
    className: maskClassName
  }), /*#__PURE__*/React.createElement("span", {
    className: panelClassName
  }, isTimedOut ? timeout.graphic : graphic, showLabel && /*#__PURE__*/React.createElement("span", {
    className: "interstitial-label"
  }, isTimedOut ? timeout.label : label)));
};

var PanelColors = ['light', 'dark'];
var PanelSizes = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
Panel.propTypes = {
  adaTimeoutAfter: PropTypes.number,
  adaTimeoutGlyph: PropTypes.string,
  adaTimeoutLabel: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.oneOf(PanelColors),
  glyph: PropTypes.string,
  inline: PropTypes.bool,
  isStatic: PropTypes.bool,
  label: PropTypes.string,
  showMask: PropTypes.bool,
  size: PropTypes.oneOf(PanelSizes)
};
Panel.defaultProps = {
  adaTimeoutAfter: 5000,
  // ms
  adaTimeoutGlyph: 'clock',
  adaTimeoutLabel: 'One Moment',
  className: undefined,
  color: 'dark',
  glyph: 'clock',
  inline: false,
  isStatic: false,
  label: 'Loading',
  showMask: false,
  size: undefined
};
Panel.displayName = 'Panel';

var _excluded = ["displayDelay", "isActive", "target"];

var Interstitial = function Interstitial(props) {
  var displayDelay = props.displayDelay,
      isActive = props.isActive,
      target = props.target,
      rest = _objectWithoutProperties(props, _excluded);

  var delayTimerRef = useRef(null);
  var targetRef = target && !target.tagName ? target : useRef(target || null);

  var _useState = useState(isActive && !displayDelay),
      _useState2 = _slicedToArray(_useState, 2),
      showInterstitial = _useState2[0],
      setShowInterstitial = _useState2[1];

  var clearTimer = function clearTimer() {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  };

  var startTimer = function startTimer() {
    clearTimer();

    if (displayDelay && displayDelay > 0) {
      delayTimerRef.current = setTimeout(function () {
        setShowInterstitial(true);
      }, displayDelay);
    }
  };

  var unmount = function unmount() {
    clearTimer();

    if (targetRef.current) {
      targetRef.current.classList.remove('position-relative', 'interstitial-target');
    }

    setShowInterstitial(false);
  }; // delay displaying component


  useEffect(function () {
    if (isActive) {
      startTimer();
    } else {
      unmount();
    }

    return function () {
      unmount();
    };
  }, [isActive]);

  if (showInterstitial) {
    if (targetRef.current) {
      targetRef.current.classList.add('position-relative', 'interstitial-target');
      return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(Panel, _extends({}, rest, {
        targetRef: targetRef,
        showMask: true
      })), targetRef.current);
    }

    return /*#__PURE__*/React.createElement(Panel, rest);
  }

  return null;
};

Interstitial.propTypes = {
  displayDelay: PropTypes.number,
  isActive: PropTypes.bool,
  target: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.shape({
    current: PropTypes.element
  })])
};
Interstitial.defaultProps = {
  displayDelay: 250,
  isActive: true,
  target: null
};
Interstitial.displayName = 'Interstitial';

export { Panel, PanelColors, PanelSizes, Interstitial as default };
//# sourceMappingURL=index.js.map
